import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

const CartContext = createContext(null);
const KEY = "bathlane_cart_v2";

// Unique line key per product + variant combination.
export const cartLineKey = (i) => `${i.id}__${i.width}__${i.finish}__${i.sink}`;
const addonLineKey = (addonId, parentLid) => `addon:${addonId}:${parentLid}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (item, qty = 1) =>
    setItems((prev) => {
      const lid = cartLineKey(item);
      const existing = prev.find((p) => p.lid === lid);
      if (existing) {
        return prev.map((p) => (p.lid === lid ? { ...p, qty: p.qty + qty } : p));
      }
      return [...prev, { ...item, type: "vanity", lid, qty }];
    });

  // Add-ons (taps / waste) attach to a parent vanity line.
  const isAddonInCart = (addonId, parentLid) =>
    items.some((p) => p.lid === addonLineKey(addonId, parentLid));

  const toggleAddon = (addon, parentLid) =>
    setItems((prev) => {
      const lid = addonLineKey(addon.id, parentLid);
      if (prev.some((p) => p.lid === lid)) {
        return prev.filter((p) => p.lid !== lid);
      }
      return [...prev, { ...addon, type: "addon", parentLid, lid, qty: 1 }];
    });

  // Removing a vanity also removes any add-ons attached to it.
  const removeItem = (lid) =>
    setItems((prev) => prev.filter((p) => p.lid !== lid && p.parentLid !== lid));

  const updateQty = (lid, qty) =>
    setItems((prev) =>
      prev.map((p) => (p.lid === lid ? { ...p, qty: Math.max(1, qty) } : p))
    );

  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((n, i) => n + i.qty, 0), [items]);
  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.price * i.qty, 0),
    [items]
  );

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQty, clear, count, subtotal, toggleAddon, isAddonInCart }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
